import { CancelledError, TimeoutError } from './errors.js';
import { withResolved } from './resolved-utils.js';

type Maybe<T> = T | undefined | null;

export interface BetterPromiseOptions {
  /**
   * Should the `abortSignal` passed to the executor be aborted if the promise was rejected.
   *
   * By default, as long as there is no point to perform any operations at the moment of rejection,
   * the signal will be aborted.
   * @default true
   */
  abortOnReject?: boolean;
  /**
   * Should the `abortSignal` passed to the executor be aborted if the promise was fulfilled.
   *
   * By default, as long as there is no point to perform any operations at the moment of resolve,
   * the signal will be aborted.
   * @default true
   */
  abortOnResolve?: boolean;
  /**
   * Signal to abort the execution.
   */
  abortSignal?: AbortSignal;
  /**
   * Execution timeout. After the timeout was reached, the promise will be aborted
   * with the `TimeoutError` error.
   */
  timeout?: number;
}

export type BetterPromiseResolveFn<T> = undefined extends T
  ? (value?: T) => void
  : (value: T) => void;

export type BetterPromiseRejectFn = (reason?: any) => void;

export type BetterPromiseRejectReason = TimeoutError | CancelledError | unknown;

interface EventMap<Result> {
  resolved: Result;
  rejected: BetterPromiseRejectReason;
  finalized:
    | { kind: 'resolved', result: Result }
    | { kind: 'rejected', reason: BetterPromiseRejectReason };
}

export interface BetterPromiseExecutorContext<Result> {
  /**
   * Abort signal. Will be aborted if the promise was rejected.
   */
  readonly abortSignal: AbortSignal;
  /**
   * @returns True if the promise was rejected.
   */
  get isRejected(): boolean;
  /**
   * @returns True if the promise was resolved.
   */
  get isResolved(): boolean;
  /**
   * Adds a new event listener to the specified event.
   * @param event - event to listen to.
   * @param listener - a corresponding callback function to call.
   */
  on<E extends keyof EventMap<Result>>(
    event: E,
    listener: (ev: EventMap<Result>[E]) => void,
  ): VoidFunction;
  /**
   * @returns Promise resolve result if it was resolved.
   */
  get result(): Result | undefined;
  /**
   * @returns Promise rejection reason if the promise was rejected.
   */
  get rejectReason(): BetterPromiseRejectReason | undefined;
  /**
   * Will throw a rejection reason if the promise was rejected.
   */
  throwIfRejected: () => void;
}

export type BetterPromiseExecutorFn<T> = (
  res: BetterPromiseResolveFn<T>,
  rej: BetterPromiseRejectFn,
  context: BetterPromiseExecutorContext<T>,
) => any;

export type BetterPromiseOnFulfilledFn<TResult1, TResult2> =
  (value: TResult1) => TResult2 | PromiseLike<TResult2>;

export type BetterPromiseOnRejectedFn<T> = (value: any) => T | PromiseLike<T>;

function withInheritedResolvers<P extends BetterPromise<any>>(
  childPromise: P,
  parentPromise: BetterPromise<any>,
): P {
  childPromise.reject = parentPromise.reject;
  childPromise.resolve = parentPromise.resolve;
  return childPromise;
}

export class BetterPromise<Result> extends Promise<Result> {
  static fn<Result>(
    fn: (context: BetterPromiseExecutorContext<Result>) => (Result | PromiseLike<Result>),
    options?: BetterPromiseOptions,
  ): BetterPromise<Awaited<Result>> {
    return new BetterPromise(async (res, rej, context) => {
      try {
        res(await fn(context));
      } catch (e) {
        rej(e);
      }
    }, options);
  }

  /**
   * @see Promise.resolve
   */
  static override resolve(): BetterPromise<void>;
  /**
   * @see Promise.resolve
   */
  static override resolve<T>(value: T | PromiseLike<T>): BetterPromise<Awaited<T>>;
  static override resolve<T>(value?: T | PromiseLike<T>): BetterPromise<Awaited<T>> {
    return this.fn(() => value) as BetterPromise<Awaited<T>>;
  }

  /**
   * @see Promise.reject
   */
  static override reject(reason?: unknown): BetterPromise<never> {
    return new BetterPromise((_, rej) => {
      rej(reason);
    });
  }


  constructor(options?: BetterPromiseOptions);
  constructor(executor?: BetterPromiseExecutorFn<Result>, options?: BetterPromiseOptions);
  constructor(
    arg1?: BetterPromiseExecutorFn<Result> | BetterPromiseOptions,
    maybeOptions?: BetterPromiseOptions,
  ) {
    let reject!: BetterPromiseRejectFn;
    let resolve!: BetterPromiseResolveFn<Result>;
    let executor: BetterPromiseExecutorFn<Result> | undefined;
    let options: BetterPromiseOptions;

    if (typeof arg1 === 'function') {
      executor = arg1;
      options = maybeOptions || {};
    } else {
      options = arg1 || {};
    }

    let resolved: [Result] | undefined;
    let rejected: [BetterPromiseRejectReason] | undefined;
    const isRejected = () => !!rejected;
    const isResolved = () => !!resolved;
    let listeners: {
      [E in keyof EventMap<Result>]?: ((data: EventMap<Result>[E]) => void)[];
    } = {};

    //#region Cleanup section.
    const cleanupFns: VoidFunction[] = [];
    const cleanup = () => {
      cleanupFns.forEach(fn => fn());
      cleanupFns.splice(0, cleanupFns.length);
      listeners = {};
    };
    //#endregion

    // We are going to use our controller signal in the executor because we can control it.
    // We can't say the same about the abort signal passed from above - we can't abort it by
    // ourselves.
    const controller = new AbortController();
    const isResolvedOrRejected = () => isResolved() || isRejected();

    super((res, rej) => {
      // Enhance resolve and reject functions with cleanup and controller abortion.
      const { abortOnResolve = true, abortOnReject = true } = options;
      resolve = ((result: Result) => {
        if (!isResolvedOrRejected()) {
          res(result);
          resolved = [result];
          listeners.resolved?.forEach(l => l(result));
          listeners.finalized?.forEach(l => l({ kind: 'resolved', result }));
          cleanup();

          if (abortOnResolve) {
            controller.abort(withResolved<Result>(result));
          }
        }
      }) as BetterPromiseResolveFn<Result>;
      reject = reason => {
        if (!isResolvedOrRejected()) {
          rej(reason);
          rejected = [reason];
          listeners.rejected?.forEach(l => l(reason));
          listeners.finalized?.forEach(l => l({ kind: 'rejected', reason }));
          cleanup();

          if (abortOnReject) {
            controller.abort(reason);
          }
        }
      };

      //#region Process abortSignal option.
      const { abortSignal } = options;
      if (abortSignal) {
        if (abortSignal.aborted) {
          return reject(abortSignal.reason);
        }
        // Whenever the passed abort signal aborts, we are also aborting our locally created
        // signal.
        const listener = () => {
          reject(abortSignal.reason);
        };
        abortSignal.addEventListener('abort', listener, true);
        cleanupFns.push(() => {
          abortSignal.removeEventListener('abort', listener, true);
        });
      }
      //#endregion

      //#region Process timeout option.
      const { timeout } = options;
      if (timeout) {
        const timeoutId = setTimeout(() => {
          reject(new TimeoutError(timeout));
        }, timeout);
        cleanupFns.push(() => {
          clearTimeout(timeoutId);
        });
      }
      //#endregion

      try {
        const voidFn = () => undefined;
        const result = executor && executor(resolve, reject, {
          abortSignal: controller.signal,
          get isRejected() {
            return isRejected();
          },
          get isResolved() {
            return isResolved();
          },
          on(event, listener) {
            // The promise may already be finalized. In this case we just call the listener.
            if (resolved || rejected) {
              if (event === 'finalized') {
                const payload = resolved
                  ? { kind: 'resolved', result: resolved[0] } as const
                  : { kind: 'rejected', reason: rejected![0] } as const;
                (
                  listener as (
                    data:
                      | { kind: 'resolved', result: Result }
                      | { kind: 'rejected', reason: BetterPromiseRejectReason },
                  ) => void
                )(payload);
              } else if (event === 'resolved' && resolved) {
                (listener as (result: Result) => void)(resolved[0]);
              } else if (event === 'rejected' && rejected) {
                (listener as (reason: BetterPromiseRejectReason) => void)(rejected[0]);
              }
              return voidFn;
            }
            listeners[event] ||= [];
            listeners[event].push(listener);

            return () => {
              const eventListeners: any[] = listeners[event] || [];
              const index = eventListeners.indexOf(listener);
              if (index >= 0) {
                eventListeners.splice(index, 1);
              }
            };
          },
          get result() {
            return resolved?.[0];
          },
          get rejectReason() {
            return rejected?.[0];
          },
          throwIfRejected() {
            if (rejected) {
              throw rejected[0];
            }
          },
        });

        // If a promise was returned, we want to handle its rejection because the JS Promise
        // will not do it for us. Not catching the promise rejection this way, an unhandled promise
        // rejection error will be thrown. We also need to perform reject properly cleaning up
        // all effects.
        if (result instanceof Promise) {
          result.catch(reject);
        }
      } catch (e) {
        // The wrapped executor may throw an error. Here we are following the same logic described
        // in result.catch() line above.
        reject(e);
      }
    });

    this.reject = reject;
    this.resolve = resolve;
  }

  /**
   * Rejects the promise with the `CancelledError` error.
   */
  cancel(): void {
    this.reject(new CancelledError());
  }

  /**
   * @see Promise.catch
   */
  override catch<CatchResult = never>(
    onRejected?: Maybe<BetterPromiseOnRejectedFn<CatchResult>>,
  ): BetterPromise<Result | CatchResult> {
    return this.then(undefined, onRejected);
  }

  /**
   * @see Promise.finally
   */
  override finally(onFinally?: Maybe<() => void>): BetterPromise<Result> {
    // Here we follow the same logic described in the "then" method.
    return withInheritedResolvers(super.finally(onFinally) as BetterPromise<Result>, this);
  }

  /**
   * Rejects the initially created promise.
   *
   * This method not only aborts the signal passed to the executor, but also rejects the
   * promise itself calling all chained listeners.
   *
   * The reason passed to the method is being passed as-is to the executor's context.
   */
  reject: BetterPromiseRejectFn;

  /**
   * Resolves the promise.
   */
  resolve: BetterPromiseResolveFn<Result>;

  /**
   * @see Promise.then
   */
  override then<A = Result, B = never>(
    onFulfilled?: Maybe<BetterPromiseOnFulfilledFn<Result, A>>,
    onRejected?: Maybe<BetterPromiseOnRejectedFn<B>>,
  ): BetterPromise<A | B> {
    // Use the original promise "then" method because in fact, it creates an AbortablePromise
    // instance.
    // Then, reassign the promise "reject" method, because not doing it and rejecting the promise
    // it will lead to an unhandled promise rejection.
    //
    // Here is an example:
    // const myPromise = new AbortablePromise(...)
    //   .catch(() => console.log('Catched'));
    //
    // If we don't reassign myPromise's "reject" method here, it will reject the promise, returned
    // from the "catch" method, which is unexpected. So, even using several catches in a row,
    // a developer will not be able to catch the error, thrown using the "reject" method.
    //
    // The expected behavior here is the "reject" method rejecting the initially created promise.
    // Then, this error will be handled via the "catch" method.
    return withInheritedResolvers(
      super.then(onFulfilled, onRejected) as BetterPromise<A | B>,
      this,
    );
  }
}